import { ChangeDetectorRef, Component, DoCheck, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CardModule } from 'primeng/card';
import { TooltipModule } from 'primeng/tooltip';
import { getTileImages, Tile } from '../../types/tile.types';
import { getAccessibleIconColor } from '../../utils/contrast-color.util';
import { getAccentBoxShadow } from '../../utils/icon-shadow.util';

const DEFAULT_ICON = 'ri-apps-2-line';
const REMIX_ICON_CLASS_PATTERN = /^ri-[a-z0-9-]+$/;

/** Reines Anzeige-Fragment einer Kachel: ein zentriertes Icon-Badge (Akzentfarbe + RemixIcon,
 * WCAG-konformer Kontrast). Wird sowohl im Portal (tools-and-trainings, klickbar zur Navigation)
 * als auch live in der Kachelverwaltung (tile-administration, editable=true — Klick auf das Icon
 * öffnet dort die RemixIcon-Auswahl) verwendet. */
@Component({
    selector: 'app-tile-preview',
    standalone: true,
    imports: [CommonModule, CardModule, TooltipModule],
    templateUrl: './tile-preview.component.html',
    styleUrls: ['./tile-preview.component.scss']
})
export class TilePreviewComponent implements DoCheck {
    @Input({ required: true }) public tile!: Partial<Tile>;
    @Input() public variant: 'default' | 'compact' = 'default';
    /** Zeigt ein Hover-Overlay auf dem Icon und macht es klickbar (nur Kachelverwaltung). */
    @Input() public editable = false;
    /** Name des aktuell ausgewählten Mandanten — für das Herkunfts-Badge bei Weblink-Kacheln
     * ("{Mandantenname}-spezifisch"), vom aufrufenden Portal-Screen übergeben statt hier per
     * Service selbst geladen, damit diese Komponente ein reines Anzeige-Fragment bleibt. */
    @Input() public mandantName?: string;
    @Output() public iconClick = new EventEmitter<void>();
    public readonly selectIconLabel = $localize`:@@tileImageSelectIcon:Icon auswählen`;
    private failedIconUrl?: string;
    private failedCoverUrl?: string;

    public get iconImageUrl(): string { return getTileImages(this.tile).iconImageUrl; }
    public get coverImageUrl(): string { return getTileImages(this.tile).coverImageUrl; }

    public get showImage(): boolean {
        return !!this.iconImageUrl && this.iconImageUrl !== this.failedIconUrl;
    }

    /** App-Icons (und generierte Icons) füllen das abgerundete Badge komplett; Logos/Favicons von
     * Weblink-Kacheln behalten ihren Rand, damit sie nicht beschnitten oder hochskaliert werden. */
    public get fillsIcon(): boolean {
        return this.tile.kind !== 'link' || !!this.tile.iconImagePrompt;
    }

    public get showCover(): boolean {
        return this.variant === 'compact' && ['cover', 'cover-icon'].includes(this.tile.imageMode ?? '') &&
            !!this.coverImageUrl && this.coverImageUrl !== this.failedCoverUrl;
    }

    public onImageError(slot: 'icon' | 'cover' = 'icon'): void {
        if (slot === 'icon') this.failedIconUrl = this.iconImageUrl;
        else this.failedCoverUrl = this.coverImageUrl;
    }

    constructor(private cdr: ChangeDetectorRef) { }

    /** PrimeNG's `p-card` läuft mit `OnPush` und überspringt deshalb die Re-Prüfung seines
     * projizierten Inhalts (Icon, Name, Subtext, Farbe), solange `tile` — wie in der
     * Kachelverwaltung während der Live-Bearbeitung — nur in-place mutiert statt durch eine neue
     * Objektreferenz ersetzt wird. Ein expliziter `detectChanges()`-Aufruf auf der eigenen
     * View erzwingt (anders als der normale Zone-getriggerte Tick) die Prüfung dieser View und
     * all ihrer Kind-Views unabhängig von deren Change-Detection-Strategie, wodurch der
     * projizierte Inhalt in p-card trotzdem live aktualisiert wird.
     */
    public ngDoCheck(): void {
        this.cdr.detectChanges();
    }

    /** Fällt auf ein Standard-Icon zurück, falls `icon` leer oder (Alt-Kacheln) noch eine Bild-URL ist. */
    public get iconClass(): string {
        const icon = this.tile.icon;
        return icon && REMIX_ICON_CLASS_PATTERN.test(icon) ? icon : DEFAULT_ICON;
    }

    /** Weiß oder Dunkelgrau, je nachdem was gegen die Akzentfarbe den besseren WCAG-Kontrast ergibt. */
    public get iconColor(): string {
        return getAccessibleIconColor(this.tile.color ?? '#6366f1');
    }

    /** Glossy-Box-Shadow des Icon-Badges, in der Akzentfarbe der Kachel statt neutralem Schwarz. */
    public get iconBoxShadow(): string {
        return this.variant === 'compact' ? 'none' : getAccentBoxShadow(this.tile.color);
    }

    /** Nur im Kachelverwaltungs-Kontext (editable) wird der Icon-Picker geöffnet — im Portal
     * (editable=false) soll der Klick auf das Icon ungehindert zur Navigation im umschließenden
     * <a> durchbubbeln, daher hier keine Bedingung im Template (die als `false` ausgewertet
     * würde und Angular sonst event.preventDefault() aufrufen ließe). */
    public onIconClick(): void {
        if (this.editable) {
            this.iconClick.emit();
        }
    }

    public onIconKeydown(event: KeyboardEvent): void {
        if (this.editable && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            this.iconClick.emit();
        }
    }

    /** `visibilityReasons` wird ausschließlich von `/api/tile/listMy` befüllt (siehe tile.types.ts)
     * — das Badge erscheint dadurch automatisch nur im Portal, nie in der Kachelverwaltung. */
    public get showVisibilityInfo(): boolean {
        return !!this.tile.visibilityReasons?.length;
    }

    /** "application" = echte, cross-app-fähige Reflact-Applikation; "link" = Weblink-Kachel, die
     * für/von einem konkreten Mandanten freigegeben wurde — daher der Mandantenname statt eines
     * generischen "vom Admin freigegeben". Ohne bekannten Mandantennamen (z.B. Kachelverwaltung)
     * bleibt ein neutraler Fallback-Text. */
    private get originLabel(): string {
        if (this.tile.kind === 'application') {
            return $localize`:@@tileOriginApplication:Reflact Applikation`;
        }
        if (this.mandantName) {
            const suffix = $localize`:@@tileOriginMandantSpecificSuffix:-spezifisch`;
            return `${this.mandantName}${suffix}`;
        }
        return $localize`:@@tileOriginAdminShared:Vom Admin freigegeben`;
    }

    private get sharingLabel(): string {
        return (this.tile.visibilityReasons ?? []).includes('individualGrant')
            ? $localize`:@@tileSharingIndividual:Individuell mit mir geteilt`
            : $localize`:@@tileSharingAll:Für alle sichtbar`;
    }

    /** Tooltip-Text des Info-Badges — erklärt Herkunft und Sichtbarkeitsgrund der Kachel. */
    public get visibilityTooltip(): string {
        const originLine = $localize`:@@tileVisibilityOriginLabel:Herkunft`;
        const sharingLine = $localize`:@@tileVisibilitySharingLabel:Sichtbarkeit`;
        return `${originLine}: ${this.originLabel}\n${sharingLine}: ${this.sharingLabel}`;
    }
}

